import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  Rate,
  DateChange,
  DateRestriction,
  DiscreteAccess,
  getInfo,
  normalizeDiscreteAccess,
  selectGroups,
  selectRates,
  selectRoomTypes,
  selectUsers,
  setGroups, setRates, setRoomTypes, setUsers,
  setCustomers,
  setCRMSettings,
  RoomType,
  AvailabilityCO,
  Services,
  setSettingsParts,
  setBilling,
  User,
} from './redux';
import { getMealTimes, isNowInMealWindow, selectHotelData } from 'pages/redux';
import Bed from '@mui/icons-material/Hotel';
import House from '@mui/icons-material/Apartment';
import Balcony from '@mui/icons-material/Balcony';
import Accessible from '@mui/icons-material/Accessible';
import AcUnit from '@mui/icons-material/AcUnit';
import Fridge from '@mui/icons-material/Kitchen';
import Family from '@mui/icons-material/FamilyRestroom';
import Tv from '@mui/icons-material/Tv';
import { toast } from 'utils/toast';
import Group from '@mui/icons-material/SwapHoriz';
import { useRooms, useRoomsData } from 'pages/Hospitality/hooks';
import { BookingDetails } from 'types';
import { syncAppDbCache } from 'utils/syncAppDbCache';
import {
  selectPermission, selectStaffId, selectUsername, setDiscreteAccess, setPermission, setStaffId,
} from 'redux/global';
import { selectUserEmail } from 'pages/Hospitality/redux';
import {
  OPTIONAL_GROUPS_MAX,
  OptionalGroup,
  addOptionalGroup as addOptionalGroupStorage,
  deleteOptionalGroup as deleteOptionalGroupStorage,
  loadOptionalGroups,
} from './optionalGroups';
import { filterRetiredDefaultGroups } from './retiredDefaultGroups';

export { syncAppDbCache, OPTIONAL_GROUPS_MAX };
export type { OptionalGroup };

export const groupMax = 6;

export const GROUP_ICON_KEYS = [
  'bed',
  'balcony',
  'house',
  'accessible',
  'ac',
  'tv',
  'fridge',
  'family',
] as const;

export type GroupIconKey = typeof GROUP_ICON_KEYS[number];

export const groupIcon = (key?: string | null) => {
  switch (key) {
    case 'bed':
      return <Bed fontSize="small" />;
    case 'balcony':
      return <Balcony fontSize="small" />;
    case 'house':
      return <House fontSize="small" />;
    case 'accessible':
      return <Accessible fontSize="small" />;
    case 'ac':
      return <AcUnit fontSize="small" />;
    case 'tv':
      return <Tv fontSize="small" />;
    case 'fridge':
      return <Fridge fontSize="small" />;
    case 'family':
      return <Family fontSize="small" />;
    default:
      return <Group fontSize="small" />;
  }
};

export type GroupOptionMode = 'add' | 'edit' | 'all';

export type GroupOption = {
  name: string;
  icon: React.ReactNode;
  onClick: (perk: string, perks: string[], setPerks: Function) => void;
  isOptional?: boolean;
  iconKey?: string;
};

export const useGroupOptions = (mode: GroupOptionMode = 'all') => {
  const groups = useAppSelector(selectGroups);
  const hotelData = useAppSelector(selectHotelData);
  const staffId = useAppSelector(selectStaffId);
  const username = useAppSelector(selectUsername);
  const userEmail = useAppSelector(selectUserEmail);
  const userKey = String(staffId || username || userEmail || 'anon');
  const hotelId = hotelData?.id;

  const [optionalGroups, setOptionalGroups] = useState<OptionalGroup[]>(() => (
    loadOptionalGroups(userKey, hotelId)
  ));

  useEffect(() => {
    setOptionalGroups(loadOptionalGroups(userKey, hotelId));
  }, [userKey, hotelId]);

  const setPerksCall = useCallback((perk: string, perks: string[], setPerks: Function) => {
    if (perks.includes(perk)) {
      setPerks(perks.filter((p) => p !== perk));
      return;
    }
    if (perks.length === groupMax) {
      toast(`you can only set ${groupMax} groups`, { type: 'error' });
    } else {
      const newPerks = [...perks];
      newPerks.push(perk);
      setPerks(newPerks);
    }
  }, []);

  const defaultGroupOptions: GroupOption[] = useMemo(() => [
    { name: '2 beds', iconKey: 'bed', icon: groupIcon('bed'), onClick: setPerksCall },
    { name: 'Large bed', iconKey: 'bed', icon: groupIcon('bed'), onClick: setPerksCall },
    { name: 'Split beds', iconKey: 'bed', icon: groupIcon('bed'), onClick: setPerksCall },
    { name: '1 bed', iconKey: 'bed', icon: groupIcon('bed'), onClick: setPerksCall },
  ], [setPerksCall]);

  const defaultNames = useMemo(
    () => defaultGroupOptions.map((g) => g.name),
    [defaultGroupOptions],
  );

  const optionalByName = useMemo(() => {
    const map = new Map<string, OptionalGroup>();
    optionalGroups.forEach((g) => map.set(g.name.toLowerCase(), g));
    return map;
  }, [optionalGroups]);

  const groupOptions: GroupOption[] = useMemo(() => {
    const seen = new Set(defaultGroupOptions.map((g) => g.name.toLowerCase()));
    const extras: GroupOption[] = [];

    const pushExtra = (name: string, isOptional?: boolean, iconKey?: string) => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return;
      seen.add(key);
      const local = optionalByName.get(key);
      const resolvedIconKey = iconKey || local?.icon;
      extras.push({
        name,
        iconKey: resolvedIconKey,
        icon: groupIcon(resolvedIconKey),
        onClick: setPerksCall,
        isOptional: Boolean(isOptional),
      });
    };

    if (mode === 'add' || mode === 'edit' || mode === 'all') {
      optionalGroups.forEach((g) => pushExtra(g.name, true, g.icon));
    }
    // Hotel-wide groups = custom tags synced from every room. Only 'all' (display/lookup)
    // needs them; edit mode must not list other rooms' tags — AddEditRoom adds this room's
    // own perks via selectableGroups extras.
    if (mode === 'all') {
      Array.from(new Set(filterRetiredDefaultGroups(groups || []))).forEach((g) => pushExtra(g, false));
    }

    return [...defaultGroupOptions, ...extras];
  }, [defaultGroupOptions, optionalGroups, optionalByName, groups, mode, setPerksCall]);

  const addOptionalGroup = useCallback((group: OptionalGroup) => {
    const result = addOptionalGroupStorage(userKey, hotelId, group, defaultNames);
    if (!result.ok) {
      toast(result.error, { type: 'warning' });
      return false;
    }
    setOptionalGroups(result.groups);
    return true;
  }, [userKey, hotelId, defaultNames]);

  const deleteOptionalGroup = useCallback((name: string) => {
    const next = deleteOptionalGroupStorage(userKey, hotelId, name);
    setOptionalGroups(next);
  }, [userKey, hotelId]);

  return {
    groupOptions,
    groups,
    optionalGroups,
    defaultNames,
    addOptionalGroup,
    deleteOptionalGroup,
  };
};

/** Calendar ranges from settings/Firestore; supports exact dates and yearly recurrence. */
export const isDateInCalendarRange = (date: Date, dateChange: DateChange) => {
  const rateDay = new Date(date);
  rateDay.setHours(12, 0, 0, 0);

  const startD = new Date(dateChange.start);
  startD.setHours(0, 0, 0, 0);
  const endD = new Date(dateChange.end);
  endD.setHours(23, 59, 59, 999);
  if (+rateDay >= +startD && +rateDay <= +endD) {
    return true;
  }

  const bookingYear = rateDay.getFullYear();
  const startAnnual = new Date(dateChange.start);
  startAnnual.setFullYear(bookingYear);
  startAnnual.setHours(0, 0, 0, 0);
  const endAnnual = new Date(dateChange.end);
  endAnnual.setFullYear(bookingYear);
  if (+endAnnual < +startAnnual) {
    endAnnual.setFullYear(bookingYear + 1);
  }
  endAnnual.setHours(23, 59, 59, 999);
  return +rateDay >= +startAnnual && +rateDay <= +endAnnual;
};

export const useUpdateSettings = () => {
  const dispatch = useAppDispatch();

  const updateLocalSettings = (part: Record<string, any>) => {
    // Lazy require — this file is imported while the store is still initializing
    // (metrics/redux → Settings/hooks → store).
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { store: appStore } = require('redux/store') as typeof import('redux/store');
    const current = (appStore.getState() as any).settings?.parts || {};
    const patch = part?.userPatch;
    if (patch && typeof patch === 'object') {
      const email = String(patch.email || '').toLowerCase();
      const users = ((current.users || []) as User[]).map((user) => (
        String(user.email || '').toLowerCase() === email
          ? { ...user, ...patch }
          : user
      ));
      const { userPatch: _drop, ...rest } = part;
      dispatch(setSettingsParts({ ...current, ...rest, users }));
    } else {
      dispatch(setSettingsParts({ ...current, ...part }));
    }
    syncAppDbCache();
  };

  return { updateLocalSettings };
};

export const useSettings = () => {
  const users = useAppSelector(selectUsers);
  const groups = useAppSelector(selectGroups);
  const roomTypes = useAppSelector(selectRoomTypes);
  const hotelData = useAppSelector(selectHotelData);
  const rates = useAppSelector(selectRates);
  const { rooms }: any = useRooms();
  const { getAllBooks, getCurrentBooking } = useRoomsData();

  const getRate = (ratePlanId: string, dt?: Date, ratePlan?: Rate) => {
    const date = dt || new Date();
    const thisRatePlan = ratePlan || rates.find((r) => r.id === ratePlanId);

    if (!thisRatePlan) return 0;

    const calendarEntry = thisRatePlan.dates.find((d) => isDateInCalendarRange(date, d));
    const calendarRate = calendarEntry ? Number(calendarEntry.rate) : 0;
    const weekendRate = [0, 6].includes(date.getDay()) ? Number(thisRatePlan.weekendRate)
      : 0;
    return calendarRate || weekendRate || Number(thisRatePlan.baseRate || 0);
  };

  const getMealNow = () => {
    const times = getMealTimes(hotelData?.prefs);
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();

    // Prefer later meal when windows overlap (same priority as legacy hour logic).
    let breakfast = '';
    let lunch = '';
    let dinner = '';
    if (isNowInMealWindow(times.breakfast.start, times.breakfast.end, nowMins)) breakfast = 'Breakfast';
    if (isNowInMealWindow(times.lunch.start, times.lunch.end, nowMins)) lunch = 'Lunch';
    if (isNowInMealWindow(times.dinner.start, times.dinner.end, nowMins)) dinner = 'Dinner';

    return dinner || lunch || breakfast;
  };

  const getIsMealServedBefore = (roomId: string, roomServices?: Services) => {
    const book = getCurrentBooking(roomId);

    const startOfTommorow = new Date();
    startOfTommorow.setDate(startOfTommorow.getDate() + 1);
    startOfTommorow.setHours(0, 0, 0, 0);

    const isBookingEndToday = +new Date(book?.endDate || 0) < +startOfTommorow;
    const isMealServedBefore = roomServices?.meals?.priorIds?.includes(`${roomId}${getMealNow().slice(0, 1)}`)
      && isBookingEndToday;

    return isMealServedBefore;
  };

  const getIsMealTime = (roomId: string, roomServices?: Services) => {
    const today = new Date().toISOString().split('T')[0];
    const isTodaysEntry = (!roomServices?.meals?.date ||
      (roomServices?.meals?.date && new Date(roomServices.meals.date)
        .toISOString().split('T')[0] === today)
    );

    const isMealTime = getCurrentBooking(roomId)?.ratePlan?.mealType.includes(getMealNow() as any)
      && (!roomServices?.meals?.ids?.includes(getMealNow())
       || !isTodaysEntry)
      && (!getIsMealServedBefore(roomId, roomServices) || !isTodaysEntry);

    return isMealTime;
  };

  const dateInfoLength = 501;

  const getDateRestrictions = (ratePlan: Rate | undefined) => {
    const dateChanges = [...(ratePlan?.dates || [])];

    type RestrictionBuild = {
      date: Date,
      rate: number,
      isCalendarDate?: boolean
    }
    const restrictionBuilds: RestrictionBuild[] = [];
    let finalBuild: RestrictionBuild | null = null;

    for (let i = 0; i < dateInfoLength; i += 1) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const calendarDate = dateChanges.find((d) => isDateInCalendarRange(date, d));

      let startD = new Date(calendarDate?.start || 0);
      startD.setFullYear(date.getFullYear());
      if (+startD < +new Date()) startD = new Date();

      const thisRate = calendarDate ? +calendarDate.rate : +(ratePlan?.baseRate || 0);
      const thisDate = calendarDate ? new Date(startD) : new Date(date);

      if (restrictionBuilds.length === 0 ||
        restrictionBuilds[restrictionBuilds.length - 1].rate !== thisRate) {
        restrictionBuilds.push({
          date: new Date(thisDate),
          rate: thisRate,
          isCalendarDate: !!calendarDate
        });
      } else if (i === dateInfoLength - 1) {
        finalBuild = { date, rate: thisRate };
      }
    }
    if (finalBuild) {
      restrictionBuilds.push(finalBuild);
    }

    const restrictions: DateRestriction[] = [];

    if (ratePlan?.weekendRate) {
      const date1 = new Date();
      date1.setHours(0, 0, 0, 0);
      const dateEnd = new Date();
      dateEnd.setDate(dateEnd.getDate() + dateInfoLength);
      restrictions.push({
        start: date1,
        end: dateEnd,
        days: ['su', 'sa'],
        rate: +ratePlan.weekendRate
      });
    }

    restrictionBuilds.forEach((b, i) => {
      if (restrictionBuilds[i + 1]) {
        const nextBuild = restrictionBuilds[i + 1];
        let dayBeforeNextBuildDate = new Date(nextBuild.date);
        dayBeforeNextBuildDate.setDate(dayBeforeNextBuildDate.getDate() - 1);
        if (+dayBeforeNextBuildDate <= +b.date) {
          dayBeforeNextBuildDate = new Date(b.date);
        }
        dayBeforeNextBuildDate.setHours(23, 59, 59, 999);
        const thisStart = new Date(b.date);
        thisStart.setHours(0, 0, 0, 0);
        restrictions.push({
          start: thisStart,
          end: dayBeforeNextBuildDate,
          ...(!b.isCalendarDate ? { days: ['mo', 'tu', 'we', 'th', 'fr'] } : {}),
          rate: b.rate
        });
      } else {
        const thisStart = new Date(b.date);
        thisStart.setHours(0, 0, 0, 0);
        const thisEnd = new Date(+b.date + 1);
        thisEnd.setHours(23, 59, 59, 999);
        restrictions.push({
          start: thisStart,
          end: thisEnd,
          ...(!b.isCalendarDate ? { days: ['mo', 'tu', 'we', 'th', 'fr'] } : {}),
          rate: b.rate
        });
      }
    });
    return restrictions;
  };

  const getRestrictionsDiff = (
    oldRatePlan: Rate,
    newRatePlan: Rate
  ) => {
    const oldRestrictions = getDateRestrictions(oldRatePlan).reverse();
    const newRestrictions = getDateRestrictions(newRatePlan).reverse();

    type RestrictionBuild = {
      date: Date,
      rate: number,
    }
    const restrictionBuilds: RestrictionBuild[] = [];

    for (let i = 0; i <= dateInfoLength; i += 1) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const earlyDate = new Date(date);
      earlyDate.setHours(0, 0, 0, 0);
      const lateDate = new Date(date);
      lateDate.setHours(23, 59, 59, 999);
      const day = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'][date.getDay()];
      const oldRestr = oldRestrictions.find((oldRestriction) => {
        return +lateDate >= +oldRestriction.start && +earlyDate <= +oldRestriction.end && (
          !oldRestriction.days || oldRestriction.days.includes(day as any));
      });

      const newRestr = newRestrictions.find((newRestriction) => {
        return +lateDate >= +newRestriction.start && +earlyDate <= +newRestriction.end && (
          !newRestriction.days || newRestriction.days.includes(day as any));
      });

      const thisRestr = newRestr?.rate === oldRestr?.rate ? undefined : newRestr;

      if (thisRestr) {
        restrictionBuilds.push({
          date,
          rate: thisRestr.rate,
        });
      }
    }

    const restrictions: DateRestriction[] = [];
    let startD: Date;

    restrictionBuilds.forEach((b, i) => {
      if (i === 0) {
        startD = new Date(b.date);
        if (restrictionBuilds.length === 1) {
          restrictions.push({
            start: startD,
            end: startD,
            rate: b.rate
          });
        }
      } else {
        const lastBuild = restrictionBuilds[i - 1];
        const lastBuildD = new Date(lastBuild.date);
        lastBuildD.setHours(0, 0, 0, 0);
        const thisBuildD = new Date(b.date);
        thisBuildD.setHours(0, 0, 0, 0);
        if (+lastBuildD !== +thisBuildD - (24 * 60 * 60 * 1000) || b.rate !== lastBuild.rate) {
          restrictions.push({
            start: startD,
            end: new Date(lastBuild.date),
            rate: lastBuild.rate
          });
          startD = new Date(b.date);
        }
        if (i === restrictionBuilds.length - 1) {
          restrictions.push({
            start: startD,
            end: new Date(b.date),
            rate: b.rate
          });
        }
      }
    });

    // console.log(restrictionBuilds, oldRestrictions, newRestrictions)
    return restrictions;
  };

  const getIsAvailabilityError = (
    roomId: string,
    date: Date,
    // eslint-disable-next-line no-unused-vars
    getBooks?: (b: BookingDetails[]) => BookingDetails[],
    addBooks?: BookingDetails[]
  ) => {
    const books00 = getAllBooks(roomId.toString());
    const books0 = getBooks?.(books00) || books00;
    const books = [...books0, ...(addBooks || [])]
      .sort((a, b) => +new Date(a.startDate) - +new Date(b.startDate));
    const dStart = new Date(date);
    dStart.setHours(23, 59, 59, 999);
    const dEnd = new Date(date);
    dEnd.setHours(0, 0, 0, 0);

    let isAvailabilityError: boolean | string = false;

    for (let i = 0; i < books.length; i += 1) {
      const book = books[i];
      const bookStart = new Date(book.startDate);
      const bookEnd = new Date(book.endDate);
      if (+dStart >= +bookStart && +dEnd <= +bookEnd) {
        isAvailabilityError = true;
        break;
      }
    }

    return isAvailabilityError;
  };

  const getFullAvailability = (
    roomType: RoomType | undefined,
    addRoomCount?: number,
    /** One id or many (bulk hold) to exclude before local onHold updates land. */
    removeRoomId?: string | string[],
    addBooks?: BookingDetails[],
    // eslint-disable-next-line no-unused-vars
    getBooks?: (b: BookingDetails[]) => BookingDetails[],
    isRemoveBooks?: boolean
  ) => {
    const availabilityCO: AvailabilityCO[] = [];
    type AvailabilityCOBuild = {
      date: Date,
      availability: number
    }
    const availabilityBuild: AvailabilityCOBuild[] = [];
    const removeIds = new Set(
      (Array.isArray(removeRoomId) ? removeRoomId : (removeRoomId ? [removeRoomId] : []))
        .map((id) => String(id)),
    );
    const thisRooms = [...(rooms || [])].filter((r) => r.roomTypeId === roomType?.id
      && !removeIds.has(String(r.id)) && !r.onHold);

    for (let i = 0; i < dateInfoLength; i += 1) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      if (i > 0) date.setHours(0, 0, 0, 0);
      let availability = addRoomCount || 0;
      thisRooms.forEach((r) => {
        const addBooks1 = addBooks?.filter((book) => book.roomId === r.id);
        if (!getIsAvailabilityError(
          r.id,
          date,
          getBooks,
          isRemoveBooks ? [] : addBooks1
        )) {
          availability += 1;
        }
      });
      if (i === 0 || availabilityBuild[availabilityBuild.length - 1].availability !== availability
        || i === dateInfoLength - 1) {
        availabilityBuild.push({
          date,
          availability
        });
      }
    }
    availabilityBuild.forEach((b, i) => {
      if (availabilityBuild[i + 1]) {
        const nextBuild = availabilityBuild[i + 1];
        let dayBeforeNextBuildDate = new Date(nextBuild.date);
        dayBeforeNextBuildDate.setDate(dayBeforeNextBuildDate.getDate() - 1);
        if (+dayBeforeNextBuildDate <= +b.date) {
          dayBeforeNextBuildDate = new Date(+b.date + 1);
        }
        availabilityCO.push({
          date_from: new Date(b.date).toISOString(),
          date_to: dayBeforeNextBuildDate.toISOString(),
          property_id: hotelData?.coId || '',
          room_type_id: roomType?.coRoomTypeId || '',
          availability: b.availability
        });
      } else {
        availabilityCO.push({
          date_from: new Date(b.date).toISOString(),
          date_to: new Date(+b.date + 1).toISOString(),
          property_id: hotelData?.coId || '',
          room_type_id: roomType?.coRoomTypeId || '',
          availability: b.availability
        });
      }
    });
    return availabilityCO;
  };

  const getAvailability = (
    roomType: RoomType | undefined,
    addBooks: BookingDetails[],
    // eslint-disable-next-line no-unused-vars
    getBooks?: (b: BookingDetails[]) => BookingDetails[],
    isRemoveBooks?: boolean
  ) => {
    type AvailabilityCOBuild = {
      start: Date,
      end: Date,
      availability: number,
    }

    const u = undefined;
    const oldAvailabilities: AvailabilityCOBuild[] = getFullAvailability(roomType).map((b) => {
      return {
        start: new Date(b.date_from),
        end: new Date(b.date_to),
        availability: b.availability,
      };
    });
    const newAvailabilities: AvailabilityCOBuild[] =
      getFullAvailability(roomType, u, u, addBooks, getBooks, isRemoveBooks).map((b) => {
        return {
          start: new Date(b.date_from),
          end: new Date(b.date_to),
          availability: b.availability,
        };
      });

    type AvailabilityBuild = {
      date: Date,
      availability: number,
    }
    const availabilityBuilds: AvailabilityBuild[] = [];

    for (let i = 0; i <= dateInfoLength; i += 1) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const earlyDate = new Date(date);
      earlyDate.setHours(0, 0, 0, 0);
      const lateDate = new Date(date);
      lateDate.setHours(23, 59, 59, 999);
      const oldAvail = oldAvailabilities.find((oldAvailability) => {
        return +lateDate >= +oldAvailability.start && +earlyDate <= +oldAvailability.end;
      });

      const newAvail = newAvailabilities.find((newAvailability) => {
        return +lateDate >= +newAvailability.start && +earlyDate <= +newAvailability.end;
      });

      const thisAvail = newAvail?.availability === oldAvail?.availability ? undefined : newAvail;

      if (thisAvail) {
        availabilityBuilds.push({
          date,
          availability: thisAvail.availability,
        });
      }
    }

    let startD: Date;

    const availabilityCO: AvailabilityCO[] = [];

    availabilityBuilds.forEach((b, i) => {
      if (i === 0) {
        startD = new Date(b.date);
        if (availabilityBuilds.length === 1) {
          availabilityCO.push({
            date_from: new Date(startD).toISOString(),
            date_to: new Date(startD).toISOString(),
            property_id: hotelData?.coId || '',
            room_type_id: roomType?.coRoomTypeId || '',
            availability: b.availability
          });
        }
      } else {
        const lastBuild = availabilityBuilds[i - 1];
        const lastBuildD = new Date(lastBuild.date);
        lastBuildD.setHours(0, 0, 0, 0);
        const thisBuildD = new Date(b.date);
        thisBuildD.setHours(0, 0, 0, 0);
        if (+lastBuildD !== +thisBuildD - (24 * 60 * 60 * 1000)
          || b.availability !== lastBuild.availability) {
          availabilityCO.push({
            date_from: new Date(startD).toISOString(),
            date_to: new Date(lastBuild.date).toISOString(),
            property_id: hotelData?.coId || '',
            room_type_id: roomType?.coRoomTypeId || '',
            availability: lastBuild.availability
          });
          startD = new Date(b.date);
        }
        if (i === availabilityBuilds.length - 1) {
          availabilityCO.push({
            date_from: new Date(startD).toISOString(),
            date_to: new Date(b.date).toISOString(),
            property_id: hotelData?.coId || '',
            room_type_id: roomType?.coRoomTypeId || '',
            availability: b.availability
          });
        }
      }
    });

    // console.log(availabilityBuilds, oldAvailabilities, newAvailabilities);
    return availabilityCO;
  };

  return {
    users,
    roomTypes,
    groups,
    rates,
    getMealNow,
    getIsMealServedBefore,
    getIsMealTime,
    getRate,
    getDateRestrictions,
    getFullAvailability,
    getAvailability,
    getRestrictionsDiff
  };
};

export const useFetchSettings = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadedHotelId, setLoadedHotelId] = useState<string | null>(null);
  const hotelData = useAppSelector(selectHotelData);
  const users = useAppSelector(selectUsers);
  const username = useAppSelector(selectUsername);
  const staffId = useAppSelector(selectStaffId);
  const userEmail = useAppSelector(selectUserEmail);
  const permission = useAppSelector(selectPermission);
  const dispatch = useAppDispatch();
  const hotelId = hotelData?.id != null ? String(hotelData.id) : null;
  const hotelIdRef = useRef(hotelId);
  hotelIdRef.current = hotelId;

  useEffect(() => {
    if (!hotelId) {
      setLoadedHotelId(null);
      return undefined;
    }
    if (loadedHotelId === hotelId && users !== null) return undefined;

    const requestedHotelId = hotelId;
    const f = async () => {
      setIsLoading(true);
      const res = await dispatch(getInfo());
      // Ignore if user switched hotels while this request was in flight.
      if (requestedHotelId !== hotelIdRef.current) return;
      setIsLoading(false);
      if (res.status === 'success') {
        dispatch(setUsers(res.data?.users ?? []));
        dispatch(setGroups(filterRetiredDefaultGroups(res.data.groups || [])));
        dispatch(setRoomTypes(res.data.roomTypes));
        dispatch(setRates(res.data.rates));
        dispatch(setBilling(res.data.billing));
        dispatch(setCRMSettings(res.data.crmSettings));
        dispatch(setCustomers(Array.isArray(res.data.customers) ? res.data.customers : []));
        setLoadedHotelId(requestedHotelId);
        syncAppDbCache();
        return;
      }
      toast(res.data, { type: 'error' });
    };

    f();
    return undefined;
  }, [hotelId, loadedHotelId, users, dispatch]);

  // Keep session permission / discrete access / staffId aligned with staff record.
  useEffect(() => {
    if (!users?.length || (!username && !userEmail)) return;
    const me = users.find((u) => (
      (userEmail && u.email?.toLowerCase() === userEmail.toLowerCase())
      || (username && u.username?.toLowerCase() === username.toLowerCase())
    ));
    if (!me || me.permission == null || me.permission === '') return;
    const staffPerm = Number(me.permission);
    if (!Number.isNaN(staffPerm) && staffPerm !== permission) {
      dispatch(setPermission(staffPerm));
    }
    if (me.id != null && String(me.id) !== '' && String(me.id) !== String(staffId || '')) {
      dispatch(setStaffId(String(me.id)));
    }
    const nextAccess = normalizeDiscreteAccess(staffPerm, me.discreteAccess as DiscreteAccess);
    const current = JSON.parse(localStorage.getItem('discreteAccess') || '{}');
    if (
      Boolean(current.inventoryEdit) !== Boolean(nextAccess.inventoryEdit)
      || Boolean(current.departmentsEdit) !== Boolean(nextAccess.departmentsEdit)
      || Boolean(current.crmEdit) !== Boolean(nextAccess.crmEdit)
    ) {
      dispatch(setDiscreteAccess(nextAccess));
    }
  }, [users, username, userEmail, permission, staffId, dispatch]);

  return { isLoading };
};
